import { useEffect } from "react";

import { isLoggedIn, ServiceError, Spinner } from "@shapr3d/frontend-lib";
import { Helmet } from "react-helmet";
import { useParams, useSearchParams } from "react-router-dom";

import { DeepLinkModal } from "../../components/DeepLinkModal/DeepLinkModal";
import ErrorPage from "../../components/ErrorPage/ErrorPage";
import { OverlayProvider } from "../../components/Headers/components/ShareModal/components/MenuPortal";
import { GenericHeader } from "../../components/Headers/GenericHeader/GenericHeader";
import { TeamsHeader } from "../../components/Headers/TeamsHeader/TeamsHeader";
import { ProjectMetadata } from "../../components/ProjectMetadata/ProjectMetadata";
import { ProjectPageFeatureTour } from "../../components/ProjectPageFeatureTour/ProjectPageFeatureTour";
import { ProjectPageHeader } from "../../components/ProjectPageHeader/ProjectPageHeader";
import { ViewLinkList } from "../../components/ViewLinkList/ViewLinkList";
import useAccount from "../../hooks/useAccount";
import { useHandleProjectInvitation } from "../../hooks/useHandleInvitation";
import { useProject } from "../../hooks/useProject";
import { useTracker } from "../../hooks/useTracker";
import { ProjectRoleUtils, SearchParam, ServiceErrorCode } from "../../types";
import { navigateToAuth } from "../../utils/authUtils";
import NotFoundPage from "../NotFoundPage/NotFoundPage";
import RequestAccessPage from "../RequestAccessPage/RequestAccessPage";
import RequestAccessPendingPage from "../RequestAccessPage/RequestAccessPendingPage";
import type { ProjectViewLink } from "../ViewLinkListPage/ViewLinkListPage";

import style from "./ProjectPage.module.css";

const ProjectPageComponent = () => {
  const { projectId } = useParams();
  const tracker = useTracker();
  const [searchParams] = useSearchParams();
  const hasInvitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";
  const { isError: isInvitationError } = useHandleProjectInvitation();
  const { data: accountData, isPending: isAccountPending } = useAccount();
  const {
    data: projectData,
    isPending: isProjectPending,
    isError: isProjectError,
    error: projectError,
  } = useProject(Boolean(hasInvitationHash));

  useEffect(() => {
    if (projectError instanceof ServiceError) {
      if (projectError.hasCode(ServiceErrorCode.ProjectNotFound)) {
        tracker.trackProjectPageLoaded({
          accessStatus: "not_found",
          projectId: projectId ?? "",
        });
        return;
      }
      if (projectError.hasCode(ServiceErrorCode.ProjectForbidden) && accountData) {
        if (!accountData.emailVerified) {
          tracker.trackProjectPageLoaded({
            accessStatus: "unverified_email",
            projectId: projectId ?? "",
          });
        } else {
          tracker.trackProjectPageLoaded({
            accessStatus: "forbidden",
            projectId: projectId ?? "",
          });
        }

        return;
      }
      if (projectError.hasCode(ServiceErrorCode.ProjectPendingAccess)) {
        tracker.trackProjectPageLoaded({
          accessStatus: "pending_access",
          projectId: projectId ?? "",
        });
        return;
      }
    }
  }, [accountData, projectError, projectId, tracker]);

  useEffect(() => {
    if (projectData) {
      tracker.trackProjectPageLoaded({
        accessStatus: "success",
        projectId: projectData.project.projectId,
        projectTeamId: projectData.teamId,
        hasReviewLink: Boolean(projectData.projectViewLinks?.length),
      });
    }
  }, [projectData, tracker]);

  if (!isLoggedIn() && !hasInvitationHash) {
    navigateToAuth();
    return null;
  }

  if (isProjectPending || isAccountPending || hasInvitationHash) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <div className={style.centerContent}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (isInvitationError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <ErrorPage />
      </div>
    );
  }

  if (
    projectError instanceof ServiceError &&
    projectError.hasCode(ServiceErrorCode.ProjectForbidden)
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <RequestAccessPage />
      </div>
    );
  }

  if (
    projectError instanceof ServiceError &&
    projectError.hasCode(ServiceErrorCode.ProjectPendingAccess)
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <RequestAccessPendingPage />
      </div>
    );
  }

  if (isProjectError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <NotFoundPage isProjectPage />
      </div>
    );
  }

  const projectViewLinks: ProjectViewLink[] =
    projectData.projectViewLinks?.map((viewLink) => ({
      title: projectData.project.name,
      projectViewLinkId: viewLink.projectViewLinkId,
      space: {
        id: "",
        spaceName: projectData.space?.spaceName,
        spaceType: "team",
      },
      uploadedAt: viewLink.updatedAt,
      uploadedByUserId: viewLink.uploadedByUserId,
    })) || [];

  return (
    <>
      <Helmet>
        <title>Shapr3D - {projectData.project.name}</title>
        <meta
          name="description"
          content="Review designs together. View models and add comments directly to your 3D design."
        />
      </Helmet>
      <OverlayProvider>
        <TeamsHeader teamId={accountData?.teams[0].id} />
        <div className={style.container}>
          <ProjectPageHeader projectData={projectData} />
          <div className={style.internalContainer}>
            <div className={style.sideContent}>
              {projectData.latestRevision && <ProjectMetadata projectData={projectData} />}
            </div>
            <div className={style.mainContent}>
              <div className={style.title}>Review link</div>
              <ViewLinkList
                viewLinks={projectViewLinks}
                users={projectData.users ?? []}
                isShortVersion={true}
                isUnpublishHidden={!ProjectRoleUtils.isAtLeast(projectData.userRole, "editor")}
                telemetryLocation="project_page"
              />
            </div>
          </div>
        </div>
        <DeepLinkModal />
        <ProjectPageFeatureTour />
      </OverlayProvider>
    </>
  );
};

export default function ProjectPage() {
  return (
    <>
      <Helmet>
        <title>Shapr3D - Project</title>
        <meta
          name="description"
          content="Review designs together. View models and add comments directly to your 3D design."
        />
      </Helmet>
      <ProjectPageComponent />
    </>
  );
}
